export const FORM_SOURCE_LOCALE = "en";

export type FormLanguage = {
  code: string;
  name: string;
  nativeName: string;
};

/** Languages a broker can add. English stays the language the form is written in. */
export const FORM_LANGUAGES: FormLanguage[] = [
  { code: "es", name: "Spanish", nativeName: "Español" },
  { code: "zh", name: "Chinese", nativeName: "中文" },
  { code: "fr", name: "French", nativeName: "Français" },
  { code: "de", name: "German", nativeName: "Deutsch" },
  { code: "pt", name: "Portuguese", nativeName: "Português" },
  { code: "vi", name: "Vietnamese", nativeName: "Tiếng Việt" },
  { code: "ko", name: "Korean", nativeName: "한국어" },
  { code: "ar", name: "Arabic", nativeName: "العربية" },
  { code: "hi", name: "Hindi", nativeName: "हिन्दी" },
  { code: "it", name: "Italian", nativeName: "Italiano" },
  { code: "ja", name: "Japanese", nativeName: "日本語" },
  { code: "pl", name: "Polish", nativeName: "Polski" },
  { code: "ru", name: "Russian", nativeName: "Русский" },
  { code: "tl", name: "Tagalog", nativeName: "Tagalog" },
  { code: "uk", name: "Ukrainian", nativeName: "Українська" },
];

const LANGUAGE_CODES = new Set(FORM_LANGUAGES.map((language) => language.code));

export function formLanguage(code: string): FormLanguage | null {
  return FORM_LANGUAGES.find((language) => language.code === code) ?? null;
}

export function isFormLanguage(code: string): boolean {
  return LANGUAGE_CODES.has(code);
}

/** `es-MX` and `es` both mean Spanish. Unknown tags are ignored. */
export function normalizeFormLocale(value: string | null | undefined): string | null {
  const trimmed = value?.trim().toLowerCase();
  if (!trimmed) return null;
  if (trimmed === FORM_SOURCE_LOCALE || trimmed.startsWith("en")) return FORM_SOURCE_LOCALE;
  if (trimmed.startsWith("zh")) return "zh";
  const primary = trimmed.split("-")[0] ?? "";
  return LANGUAGE_CODES.has(primary) ? primary : null;
}

/**
 * A link language wins. Otherwise the first browser language the form offers.
 * English is always available.
 */
export function matchFormLocale(input: {
  requested: string | null | undefined;
  browserLanguages: readonly string[];
  available: readonly string[];
}): string {
  const offered = new Set(input.available);
  const requested = input.requested?.trim().toLowerCase();
  if (requested === FORM_SOURCE_LOCALE || requested?.startsWith("en")) {
    return FORM_SOURCE_LOCALE;
  }
  const requestedCode = normalizeFormLocale(requested);
  if (requestedCode && offered.has(requestedCode)) return requestedCode;
  if (requested) return FORM_SOURCE_LOCALE;
  for (const language of input.browserLanguages) {
    const code = normalizeFormLocale(language);
    if (code && code !== FORM_SOURCE_LOCALE && offered.has(code)) return code;
  }
  return FORM_SOURCE_LOCALE;
}

export const FORM_CHROME_SOURCES = [
  "Required",
  "Loading…",
  "Saving…",
  "Years",
  "Months",
  "Years or months",
  "Number of years",
  "Number of months",
  "Add item",
  "Remove entry",
  "This answer is required.",
  "Unable to save your answers.",
  "Your business",
  "Revenue",
  "Motivation",
  "Timeline",
  "How to reach you",
  "Your details",
  "Thank you",
  "How can we reach you?",
  "Continue",
  "Back",
  "Submit",
  "Done",
  "Talk with {First}",
  "Your details are only shared with {broker}.",
] as const;
